import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import clsx from "clsx";
import { Children, useEffect, useRef, useState } from "react";
import { IconButton } from "./IconButton";
import { ChevronLeftOutline, ChevronRightOutline } from "../icons/icons";
import { SlotsProvider } from "../providers/SlotsProvider";
import { useDefaultProps } from "../providers/utils/defaultProps";
import { mergeSx, useDebounce } from "../system";
import { Box } from "./Box";
import { Stack } from "./Stack";
export const horizontalScrollerClasses = {
    root: "MinervaHorizontalScroller-root",
    "child-wrapper": "MinervaHorizontalScroller-childWrapper",
};
export const horizontalScrollerSlots = {
    rightArrow: ChevronRightOutline,
    leftArrow: ChevronLeftOutline,
    rightButton: IconButton,
    leftButton: IconButton,
    scrollContainer: Stack,
};
export const HorizontalScroller = (props) => {
    const { scrollAmount, activeIndex, hideScrollButtons, showScrollbar, slots, slotProps, className, sx, children, ...rest } = useDefaultProps({
        component: "HorizontalScroller",
        props,
    });
    const [scrollX, setScrollX] = useState(0);
    const [scrollEnd, setScrollEnd] = useState(true);
    const scrollRef = useRef(null);
    const checkScrollEnd = useDebounce(() => {
        if (!scrollRef?.current) {
            return;
        }
        setScrollX(scrollRef.current.scrollLeft);
        if (Math.floor(scrollRef.current.scrollWidth - scrollRef.current.scrollLeft) <=
            scrollRef.current.offsetWidth) {
            setScrollEnd(true);
        }
        else {
            setScrollEnd(false);
        }
    }, 25);
    const scroll = (direction) => {
        if (!scrollRef?.current) {
            return;
        }
        const scrollDelta = scrollAmount ?? scrollRef.current.offsetWidth;
        const scrollAmountAndDirection = direction === "left" ? -scrollDelta : scrollDelta;
        scrollRef.current.scrollTo({
            left: scrollRef.current.scrollLeft + scrollAmountAndDirection || scrollX,
            behavior: "smooth",
        });
        setScrollX(scrollX + scrollAmountAndDirection);
        checkScrollEnd();
    };
    const scrollTo = (distanceFromLeft) => {
        if (!scrollRef?.current) {
            return;
        }
        scrollRef.current.scrollTo({
            left: distanceFromLeft,
            behavior: "smooth",
        });
        setScrollX(distanceFromLeft || scrollX);
        checkScrollEnd();
    };
    useEffect(() => {
        checkScrollEnd();
        if (scrollRef.current && activeIndex !== undefined) {
            const activeChild = scrollRef.current.children[activeIndex];
            if (activeChild) {
                const scrollAmount = Math.floor(activeChild.getBoundingClientRect().left -
                    scrollRef.current.getBoundingClientRect().left +
                    scrollRef.current.scrollLeft);
                scrollTo(scrollAmount);
            }
        }
    }, [activeIndex, checkScrollEnd, scrollTo, scrollRef.current]);
    return (_jsx(SlotsProvider, { baseSlots: horizontalScrollerSlots, slots: slots, children: ({ rightArrow: RightArrow, leftArrow: LeftArrow, rightButton: RightButton, leftButton: LeftButton, scrollContainer: ScrollContainer, }) => (_jsxs(Stack, { className: clsx([horizontalScrollerClasses.root, className]), flexDirection: "row", alignItems: "center", width: "100%", sx: mergeSx({
                position: "relative",
            }, sx), ...rest, children: [hideScrollButtons !== true && scrollX !== 0 && (_jsx(LeftButton, { variant: "ghost", color: "surface", density: "dense", onClick: (e) => {
                        e.stopPropagation();
                        scroll("left");
                    }, ...slotProps?.leftButton, sx: mergeSx({
                        flexShrink: 0,
                        marginRight: 1,
                        zIndex: 1,
                        borderRadius: (theme) => theme.radius.full,
                        breakpoints: {
                            xs: {
                                display: hideScrollButtons === "mobile-only"
                                    ? "none"
                                    : "flex",
                            },
                            md: {
                                display: hideScrollButtons === "desktop-only"
                                    ? "none"
                                    : "flex",
                            },
                        },
                    }, slotProps?.leftButton?.sx), children: _jsx(LeftArrow, { size: "xs", ...slotProps?.leftArrow }) })), _jsx(ScrollContainer, { ref: scrollRef, onScroll: checkScrollEnd, ...slotProps?.scrollContainer, sx: mergeSx({
                        position: "relative",
                        display: "flex",
                        flexDirection: "row",
                        width: "100%",
                        flexWrap: "nowrap",
                        maxWidth: "100%",
                        overflowX: "auto",
                        scrollBehavior: "smooth",
                        "&::-webkit-scrollbar": !showScrollbar
                            ? {
                                display: "none",
                                background: "transparent",
                                "-webkit-appearance": "none",
                                width: 0,
                                height: 0,
                            }
                            : {},
                    }, slotProps?.scrollContainer?.sx), children: Children.map(children, (child) => (_jsx(Box, { className: horizontalScrollerClasses["child-wrapper"], onClick: (e) => (scrollX !== 0 || !scrollEnd) &&
                            scrollTo(e.currentTarget.offsetLeft), children: child }))) }), hideScrollButtons !== true && !scrollEnd && (_jsx(RightButton, { variant: "ghost", color: "surface", density: "dense", onClick: (e) => {
                        e.stopPropagation();
                        scroll("right");
                    }, ...slotProps?.rightButton, sx: mergeSx({
                        flexShrink: 0,
                        marginLeft: 1,
                        borderRadius: (theme) => theme.radius.full,
                        breakpoints: {
                            xs: {
                                display: hideScrollButtons === "mobile-only"
                                    ? "none"
                                    : "flex",
                            },
                            md: {
                                display: hideScrollButtons === "desktop-only"
                                    ? "none"
                                    : "flex",
                            },
                        },
                    }, slotProps?.rightButton?.sx), children: _jsx(RightArrow, { size: "xs", ...slotProps?.rightArrow }) }))] })) }));
};
//# sourceMappingURL=HorizontalScroller.js.map